@let eventData = event$ | async;
@let isFreeEvent = eventData?.event_type === "free";
@let isFreePrivateEvent = isFreeEvent && !!eventData?.is_private;
@let marketingOnly = eventData?.role === "marketing";
@let coordinatorOnly = eventData?.role === "pdv_portaria_coordenador";
@let readOnly = eventData?.role === "observer";

<app-layout [isProducerArea]="true">
  <app-page-header
    [title]="'Detalhes do evento'"
    [titleIcon]="'pi pi-calendar'"
    [subtitle]="'Visualize e gerencie os detalhes e relatórios de vendas do seu evento.'"
    [breadcrumbs]="[
      { label: 'Início', link: '/', icon: 'pi pi-home' },
      { label: 'Área do produtor', link: '/producer-area', icon: 'pi pi-chart-bar' },
      { label: 'Detalhes do evento', icon: 'pi pi-calendar' },
    ]">
    <div class="report__header">
      <select class="form-select" aria-label="Selecione o evento" (change)="selectEvent($event)">
        @for (event of myEvents$ | async; track $index) {
          <option [value]="event.id" [selected]="eventId?.toString() === event.id.toString()">{{ event.title }}</option>
        }
      </select>

      <div class="report__header-buttons">
        <app-button (btnClick)="goToEventPage()" icon="pi pi-external-link">Página do evento</app-button>
        @if (eventData && !marketingOnly && !coordinatorOnly && !readOnly) {
          @if (eventData.status === "cancelled") {
            <app-button [disabled]="true" styleType="danger" icon="pi pi-ban">Evento cancelado</app-button>
          } @else {
            <app-button
              (btnClick)="cancelEvent(!!isFreeEvent)"
              styleType="danger"
              icon="pi pi-times"
              [disabled]="eventData.status !== 'published'">
              Cancelar evento
            </app-button>
          }
        }
      </div>
    </div>
  </app-page-header>
  <ng-container>
    <div class="report">
      <div class="page-content">
        <section class="report__main-content">
          <div class="report__event-card-container">
            @if (eventData; as event) {
              <app-event-card
                [event]="event"
                [fullWidth]="true"
                [isProducerArea]="true"
                [showEditButton]="!marketingOnly && !coordinatorOnly && !readOnly" />
            } @else {
              <p-skeleton width="100%" height="300px" />
            }
          </div>

          @if (eventData && !marketingOnly && !coordinatorOnly && !isFreePrivateEvent) {
            <article class="card report__event-tickets">
              <app-section-header title="Ingressos" />
              <div class="report__event-tickets__container row">
                @if (!isFreeEvent) {
                  <div class="report__sales-summary col-12 col-xl-8 mb-4">
                    @if (summary$ | async; as summary) {
                      <app-data-card
                        [data]="(totalSalesValue(summary) | currency: 'BRL') ?? 'R$ 0,00'"
                        icon="currency"
                        title="Total de Vendas"
                        size="small"
                        collapsibleInfo="Faturamento bruto das vendas pagas (valor de face dos ingressos, sem abater cupom ou taxas)." />
                      <app-data-card
                        [data]="(summary.refunded.value | currency: 'BRL') ?? 'R$ 0,00'"
                        icon="currency"
                        title="Vendas Reembolsadas"
                        size="small"
                        collapsibleInfo="Todas as vendas que foram devolvidas ao comprador, mediante solicitação de reembolso." />
                      <app-data-card
                        [data]="(couponDiscountValue(summary) | currency: 'BRL') ?? 'R$ 0,00'"
                        icon="currency"
                        title="Cupom de Desconto"
                        size="small"
                        collapsibleInfo="Total de descontos concedidos via cupom nas vendas pagas. Esse valor é absorvido pelo organizador." />
                      <app-data-card
                        [data]="(pdvPortariaDebitValue(summary) | currency: 'BRL') ?? 'R$ 0,00'"
                        icon="currency"
                        title="Comissão PDV Evento"
                        size="small"
                        collapsibleInfo="Taxa da plataforma absorvida pelo organizador nas vendas de PDV Evento." />
                      @if (eventData.status === "published" && financialContributionValue(summary) > 0) {
                        <app-data-card
                          [data]="(financialContributionValue(summary) | currency: 'BRL') ?? 'R$ 0,00'"
                          icon="currency"
                          title="Aporte financeiro"
                          size="small"
                          collapsibleInfo="Valor acordado de aporte financeiro neste evento. Esse valor é descontado do saldo líquido." />
                      }
                      <app-data-card
                        [data]="(summary.valid_sales.value | currency: 'BRL') ?? 'R$ 0,00'"
                        icon="currency"
                        title="Saldo Líquido"
                        size="small"
                        [inverted]="true"
                        collapsibleInfo="Valor líquido do organizador: total de vendas menos cupom, comissão do PDV Evento, taxas absorvidas e, quando houver, o aporte financeiro acordado." />
                    } @else {
                      <p-skeleton height="75px" />
                      <p-skeleton height="75px" />
                      <p-skeleton height="75px" />
                      <p-skeleton height="75px" />
                      <p-skeleton height="75px" />
                    }
                  </div>
                }

                <app-batches-report-table [batches]="(batches$ | async) || []" class="col-12 col-xl-4" />
              </div>
            </article>
          }
        </section>

        @if (!eventData) {
          <section class="report__tabs-container">
            <p-skeleton width="100%" height="320px" />
          </section>
        } @else {
          @for (_ of [eventId]; track eventId) {
            <section class="report__tabs-container">
              <p-tabs [(value)]="activeTab" [scrollable]="true">
                <p-tablist>
                  @if (!marketingOnly && !coordinatorOnly && !isFreeEvent) {
                    <p-tab value="0">Visão 360</p-tab>
                    <p-tab value="1">Resumo Vendas</p-tab>
                  }
                  @if (!marketingOnly && !coordinatorOnly) {
                    <p-tab value="2">Relatório Validados</p-tab>
                    @if (!isFreePrivateEvent) {
                      <p-tab value="3">Pedidos</p-tab>
                    }
                    <p-tab value="4">Convidados</p-tab>
                  }
                  @if (!marketingOnly) {
                    <p-tab value="5">Colaboradores</p-tab>
                    <p-tab value="8">Caixas PDV</p-tab>
                  }
                  @if (!marketingOnly && !coordinatorOnly) {
                    @if (!isFreeEvent) {
                      <p-tab value="6">Cupons de Desconto</p-tab>
                    }
                  }
                  @if (!isFreeEvent && !coordinatorOnly) {
                    <p-tab value="7">Integrações Social Media</p-tab>
                  }
                </p-tablist>
                <p-tabpanels>
                  @if (!isFreeEvent && !coordinatorOnly) {
                    <p-tabpanel value="7">
                      @if (eventId && activeTab === "7") {
                        <app-marketing-panel
                          [eventId]="eventId"
                          [eventName]="eventData.title || ''"
                          [hideMoney]="marketingOnly"
                          [readOnly]="readOnly" />
                      }
                    </p-tabpanel>
                  }
                  @if (!marketingOnly && !coordinatorOnly && !isFreeEvent) {
                    <p-tabpanel value="0">
                      <app-report-overview [summary]="summary$ | async" [sellerTotal]="sellerTotal$ | async" />
                    </p-tabpanel>
                    <p-tabpanel value="1">
                      <app-sales-summary-tab />
                    </p-tabpanel>
                  }
                  @if (!marketingOnly && !coordinatorOnly) {
                    <p-tabpanel value="2">
                      <app-validated-summary-tab />
                    </p-tabpanel>
                    @if (!isFreePrivateEvent) {
                      <p-tabpanel value="3">
                        <app-participants-tab />
                      </p-tabpanel>
                    }
                    <p-tabpanel value="4">
                      <app-guests-tab [eventId]="eventId" [readOnly]="readOnly" />
                    </p-tabpanel>
                  }
                  @if (!marketingOnly) {
                    <p-tabpanel value="5">
                      <app-employees-tab
                        [eventId]="eventId"
                        [operatorsOnly]="coordinatorOnly"
                        [readOnly]="readOnly"
                        [canGeneratePin]="eventData.can_generate_pdv_pin === true" />
                    </p-tabpanel>
                    <p-tabpanel value="8">
                      @if (eventId && activeTab === "8") {
                        <app-pdv-sessions-tab [eventId]="eventId" />
                      }
                    </p-tabpanel>
                  }
                  @if (!marketingOnly && !coordinatorOnly) {
                    @if (!isFreeEvent) {
                      <p-tabpanel value="6">
                        @if (eventId) {
                          <app-producer-coupons-page [eventId]="eventId" [readOnly]="readOnly" />
                        }
                      </p-tabpanel>
                    }
                  }
                </p-tabpanels>
              </p-tabs>
            </section>
          }
        }
      </div>
    </div>
  </ng-container>
</app-layout>
